class PredictiveSearch extends HTMLElement {
  constructor() {
    super()
    this.cachedResults = {}
    this.input = this.querySelector('input[type="search"]')
    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]'
    )
    this.predictiveSearchResultsInitial = this.querySelector(
      '[data-predictive-search]'
    ).innerHTML
    this.isOpen = false
    this.clearBtn = this.querySelector('.js-search-clear')
    this.form = this.querySelector('form.search')
    this.redirectResult = false
    this.noResults = false
    this.doneTyping = false
    this.setupEventListeners()
  }

  setupEventListeners() {
    const _this = this

    const onChange = debounce((event) => {
      _this.onSearch(event)
    }, 300).bind(this)

    _this.input.addEventListener('input', onChange)
    _this.input.addEventListener('focus', _this.onFocus.bind(this))
    _this.addEventListener('focusout', _this.onFocusOut.bind(this))
    _this.clearBtn.addEventListener('click', _this.clearSearch.bind(this))
    _this.input.addEventListener('keyup', _this.setInputFocus.bind(this))
    _this.input.addEventListener('focusout', _this.removeInputFocus.bind(this))
  }

  setInputFocus(event) {
    if ((event.shiftKey && event.key === 'Tab') || event.key === 'Tab') {
      this.form.classList.add('is-clicked')
    }
    this.doneTyping = false
    let timer
    clearTimeout(timer)

    timer = setTimeout(() => {
      this.doneTyping = true
    }, 1000)
  }

  removeInputFocus() {
    this.form.classList.remove('is-clicked')
  }

  clearSearch(event) {
    event.stopPropagation()
    this.input.value = ''
    this.input.focus()
    this.predictiveSearchResults.innerHTML = this.predictiveSearchResultsInitial
  }

  isMobileDevice() {
    return window.matchMedia('(max-width: 990px)').matches
  }

  getQuery() {
    return this.input.value.trim()
  }

  onSearch() {
    const searchTerm = this.getQuery()
    if (!searchTerm.length) {
      this.predictiveSearchResults.innerHTML =
        this.predictiveSearchResultsInitial
      this.close(true)
      return
    }

    if (this.redirectResult) {
      this.redirectSearchResultPage(searchTerm)
    } else {
      this.getSearchResults(searchTerm)
    }
  }

  onFocus() {
    const searchTerm = this.getQuery()

    if (!searchTerm.length) return

    if (this.getAttribute('results') === 'true') {
      this.open()
    } else {
      if (!this.isMobileDevice()) this.getSearchResults(searchTerm)
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close()
    })
  }

  getSearchResultData(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase()
    this.setLiveRegionLoadingState()

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status)
          this.close()
          throw error
        }

        return response.text()
      })
      .then((text) => {
        const noResults = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('div[data-no-search-results]')
          ?.getAttribute('data-no-search-results')

        this.noResults = noResults

        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML
        this.renderSearchResults(resultsMarkup)
      })
      .catch((error) => {
        this.close()
        throw error
      })
  }

  redirectSearchResultPage(searchTerm) {
    fetch(
      `${routes.predictive_search_url}.json?q=${encodeURIComponent(searchTerm)}`
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status)
          this.close()
          throw error
        }

        return response.json()
      })
      .then((data) => {
        const results = data?.resources?.results || {}
        if (results?.collections?.length) {
          location.href = `/search?q=${encodeURIComponent(searchTerm)}`
        } else if (results?.queries?.length) {
          location.href = `/search?q=${encodeURIComponent(searchTerm)}`
        } else {
          location.href = `/search?_pos=1&_psq=${encodeURIComponent(searchTerm)}&_ss=e&_v=1.0&q=${encodeURIComponent(searchTerm)}`
        }
      })
      .catch((error) => {
        this.close()
        throw error
      })
  }

  getSearchResults(searchTerm) {
    this.getSearchResultData(searchTerm)
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status')
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text')

    this.setLiveRegionText(this.loadingText)
    this.setAttribute('loading', true)
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false')
    this.statusElement.textContent = statusText

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true')
    }, 1000)
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup
    this.setAttribute('results', true)

    this.setLiveRegionResults()
    this.open()
  }

  setLiveRegionResults() {
    this.removeAttribute('loading')
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')
        .textContent
    )
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.getElementById('shopify-section-header').getBoundingClientRect()
        .bottom
    return this.resultsMaxHeight
  }

  open() {
    this.setAttribute('open', true)
    this.input.setAttribute('aria-expanded', true)
    this.isOpen = true
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = ''
      this.removeAttribute('results')
      this.removeAttribute('open')
    }

    const selected = this.querySelector('[aria-selected="true"]')

    if (selected) selected.setAttribute('aria-selected', false)

    this.input.setAttribute('aria-activedescendant', '')
    this.removeAttribute('open')
    this.input.setAttribute('aria-expanded', false)
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style')

    this.isOpen = false
  }
}

customElements.define('predictive-search', PredictiveSearch)

class SearchPopover extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('#predictive_search_input')

    this.addEventListener('toggle', (event) => {
      const open = event.newState == 'open'
      const headerWrapper = document.querySelector('.header-wrapper')
      const header = document.querySelector('header.header')

      headerWrapper.classList.toggle('header-wrapper--search-open', open)

      if (open) {
        document.body.classList.add('scroll-lock')
        header
          .querySelector('.header__inline-menu')
          ?.setAttribute('inert', true)
        document.querySelector('main')?.setAttribute('inert', true)
        input?.focus()
      } else {
        document.body.classList.remove('scroll-lock')
        header.querySelector('.header__inline-menu')?.removeAttribute('inert')
        document.querySelector('main')?.removeAttribute('inert')
      }
    })

    const trigger = document.querySelector("[popovertarget='search_popover']")

    // For ios, focus must be added via user gesture (directly in the function,
    // not in raf/settimeout) to make keyboard open on mobile. The popver is
    // display: none at the time of user gesture, so we can't do that. Instead,
    // we're focusing a fake input outside of the popover to make the keyboard
    // open, and then shifting focus to search input
    trigger.addEventListener('pointerdown', async (event) => {
      const fakeInput  = document.querySelector("#placeholder-input");
      if (!fakeInput) return;
      fakeInput.classList.remove("hidden")
      fakeInput.focus({ preventScroll: true })
      fakeInput.click()
      event.preventDefault();
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(true)
        }, 500)
      })
      input.focus({ preventScroll: true })
      input.click()
      fakeInput.classList.add("hidden")

    })
  }
}

customElements.define('search-popover', SearchPopover)
